import { Pipe, PipeTransform } from '@angular/core';
import { select, Store } from '@ngrx/store';
import {
  selectFilePrices,
  selectFilesInBasket,
  selectPackagesInBasket,
  selectPhotoGroups,
  selectPhotosList,
  selectPriceToAddToEveryFileInProduct,
  selectPriceToAddToFirstPrint,
  selectPrintsInBasket,
  selectProductsInBasket,
  selectProductsList,
} from '../../actions/selector';
import {
  listFilePriceEntitlements,
  ShoppingCartState,
} from '../../actions/standalone';
import { calculateProductPriceFromParams } from '../../../products-front/helpers';
import { PriceConfig } from '../../../types';
import { AvailableCurrenciesService } from '../../../../../zalamo/currencies/available-currencies.service';
import { map } from 'rxjs/operators';
import { combineLatest } from 'rxjs';
import { singleStepPriceCalculator } from '@ui/step-pricing/StepPriceCalculator';
import { StepPricingMode } from '../../../../../../../../photographer-panel/src/graphql';

@Pipe({
    name: 'productPrice',
    standalone: false
})
export class ProductPricePipe implements PipeTransform {
  constructor(
    private store: Store<{ shoppingCart: ShoppingCartState }>,
    private availableCurrencies: AvailableCurrenciesService,
  ) {}

  transform(selectedProductId: string) {
    return combineLatest([
      this.store.pipe(select(selectProductsInBasket)),
      this.store.pipe(select(selectProductsList)),
      this.store.pipe(select(selectPriceToAddToEveryFileInProduct)),
      this.store.pipe(select(selectPhotosList)),
      this.store.pipe(select(selectPhotoGroups)),
      combineLatest([
        this.store.pipe(select(selectFilesInBasket)),
        this.store.pipe(select(selectFilePrices)),
        this.store.pipe(select(selectPriceToAddToFirstPrint)),
        this.store.pipe(select(selectPrintsInBasket)),
        this.store.pipe(select(selectPackagesInBasket)),
      ]),
    ]).pipe(
      map(
        ([
          productsInBasket,
          productsInShop,
          priceToAddToEveryFileInProduct,
          photosList,
          photoGroups,
          [
            filesInBasket,
            filePrices,
            priceToAddToFirstPrint,
            printsInBasket,
            packagesInBasket,
          ],
        ]) => {
          const productInBasket = productsInBasket.find(
            (value) => value.selectedProductId === selectedProductId,
          );
          const productIndex = productsInBasket.findIndex(
            (value) => value.selectedProductId === selectedProductId,
          );
          const price: Array<PriceConfig> = [];
          const productInShop = productsInShop.find(
            (value1) => value1.productId === productInBasket?.productId,
          );
          const filePriceEntitlements = listFilePriceEntitlements({
            photoGroups,
            photoGroupList: photosList,
            priceToAddToFirstPrint,
            priceToAddToEveryFileInProduct,
            filePrices,
            printsInBasket,
            filesInBasket,
            productsInBasket,
            packagesInBasket,
          });
          if (productInBasket && productInShop) {
            price.push({
              price:
                calculateProductPriceFromParams(
                  productInShop,
                  productInBasket.configuration,
                  this.availableCurrencies.getCurrentCurrency(),
                ) * productInBasket.quantity,
              code: this.availableCurrencies.getCurrentCurrency(),
            });
            productInBasket.photoIdList
              .filter(
                ({ photoId }, photoIndex, photoIdList) =>
                  photoIdList.findIndex((item) => item.photoId === photoId) ===
                  photoIndex,
              )
              .forEach(({ photoId }) => {
                const config = photoGroups.find(
                  ({ photoGroupId }) =>
                    photoGroupId ===
                    photosList
                      .find(({ photos }) =>
                        photos.find((p) => p.photoId === photoId),
                      )
                      .photos.find((p) => p.photoId === photoId).photoGroupId,
                ).config;
                const supplement =
                  config?.individualSellSettings &&
                  config?.sellProducts &&
                  config?.customProductsConfig
                    ? config.priceToAddToEveryFileInProduct
                    : priceToAddToEveryFileInProduct;
                if (
                  !productsInBasket.find(
                    ({ photoIdList }, i) =>
                      i < productIndex &&
                      photoIdList.find(({ photoId: pId }) => pId === photoId),
                  ) &&
                  supplement.addPriceToEveryFileInProduct &&
                  !(
                    (supplement.skipAddToEveryFileInProductIfFileBought &&
                      filesInBasket.find(
                        (f) => f.photoId === photoId && !f.togetherWithProduct,
                      )) ||
                    (!supplement.skipAddToEveryFileInProductIfFileBought &&
                      filesInBasket.find(
                        (f) =>
                          f.photoId === photoId &&
                          !!f.togetherWithPrint &&
                          !!f.togetherWithLookBookProduct,
                      ))
                  )
                ) {
                  if (supplement.countWithFilePrices) {
                    const entitlement = filePriceEntitlements.find(
                      (item) =>
                        item.photoId === photoId && item.type === 'product',
                    );
                    if (!entitlement) {
                      return;
                    }
                    const filesPrice =
                      config.individualSellSettings && config.customFilesPricing
                        ? config.files
                        : filePrices;
                    const filePrice = filesPrice.find(
                      ({ filePricingId }) =>
                        filePricingId === entitlement.filePricingId,
                    );
                    const pricingList = filePriceEntitlements.filter(
                      (item) =>
                        item.filePricingId === entitlement.filePricingId,
                    );
                    const entitlementIndex = pricingList.findIndex(
                      (item) =>
                        item.photoId === photoId && item.type === 'product',
                    );
                    if (!filePrice || entitlementIndex < 0) {
                      return;
                    }
                    price.find(
                      ({ code }) =>
                        code === this.availableCurrencies.getCurrentCurrency(),
                    ).price +=
                      singleStepPriceCalculator(
                        entitlementIndex + 1,
                        pricingList.length,
                        !filePrice.stepPricingEnabled ||
                          filePrice.stepPricing.mode === 'None'
                          ? {
                              ...filePrice.stepPricing,
                              mode: StepPricingMode.None,
                            }
                          : filePrice.stepPricing,
                        filePrice.price,
                      ).find(
                        ({ code }) =>
                          code ===
                          this.availableCurrencies.getCurrentCurrency(),
                      )?.price || 0;
                  } else {
                    price.find(
                      ({ code }) =>
                        code === this.availableCurrencies.getCurrentCurrency(),
                    ).price += supplement.price
                      ? supplement.price.find(
                          ({ code }) =>
                            code ===
                            this.availableCurrencies.getCurrentCurrency(),
                        ).price
                      : 0;
                  }
                }
              });
          }
          return price;
        },
      ),
    );
  }
}
